import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { aiAgentApi } from '../../lib/aiAgentApi';
import { isAuthenticated } from '../../lib/auth';
import { AiAgentDrawerV2 } from './v2/AiAgentDrawerV2';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Sparkles } from 'lucide-react';
import './ai-agent.css';

const HIDDEN_ROUTES = ['/login', '/forgot-password', '/reset-password'];

/**
 * Top-nav AI Agent launcher.
 *
 * - Replaces the old floating bubble so the agent never overlaps other
 *   drawers / modals.
 * - Tooltip introduces the persona ("Hi I'm <Name>…") on hover.
 * - Custom sparkle icon over a soft gradient; subtle pulse to attract.
 */
export const AiAgentNavLauncher = () => {
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (HIDDEN_ROUTES.includes(location.pathname)) { setChecked(true); return; }
    if (!isAuthenticated()) { setChecked(true); return; }
    aiAgentApi.me()
      .then((d) => setMe(d))
      .catch(() => setMe(null))
      .finally(() => setChecked(true));
  }, [location.pathname]);

  if (!checked || !me?.ai_agent_access) return null;
  if (HIDDEN_ROUTES.includes(location.pathname)) return null;

  const personaName = me?.persona_name || 'your assistant';

  return (
    <>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="ai-agent-nav-launcher"
              aria-label={`Open ${personaName}`}
              className="ai-launcher-btn group relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-red-500 via-red-600 to-rose-700 text-white shadow-sm transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1"
            >
              <Sparkles className="h-4 w-4 drop-shadow-sm" strokeWidth={2.2} />
              <span className="ai-launcher-pulse" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" sideOffset={8} className="max-w-[260px] p-3 text-xs">
            <p className="font-semibold mb-0.5">Hi, I'm {personaName}.</p>
            <p className="text-muted-foreground">
              Happy to answer your questions about the platform, help you navigate, or generate reports for you.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <AiAgentDrawerV2 open={open} onOpenChange={setOpen} me={me} />
    </>
  );
};

export default AiAgentNavLauncher;
